import { event } from "@foretagsplatsen/yaem";
import AlertCollector from "../alerts/AlertCollector.js";

/**
 * `AbstractWizardStep` is a class representing a step in a wizard.
 *
 * To start the step, the client should send `start()` to the
 * receiver. This method receives an optional `context` as argument
 * (can be anything) containing information for the step and that the
 * step can enrich with its own information. The wizard model does not
 * care about the context and just passes it along the steps.
 *
 * When a step is started, the client can send `canBeSubmitted()` to the
 * receiver to know if the step is ready to be moved on. The step may
 * send the `completed` event to notify the listeners that the value
 * of `canBeSubmitted()` may have changed. This should happen for example
 * if the step is rendered with a form and all required form fields
 * have just been filled.
 *
 * When a step is completed, the client should send `submit()` at
 * which point the `submitted` event is triggered. After submission is
 * finished, either the `submitSuccess` or `submitException` will be
 * asynchronously triggered with no argument. In case of exceptions,
 * the method `getSubmitExceptions()` should be used to know what
 * happened.
 *
 * Some steps may auto-submit when the client sends `start()`. The
 * client can prevent auto-submit by passing an appropriate parameter.
 *
 * A step must have some kind of interaction with the user: either
 * asking for data or presenting some data. Even steps using
 * auto-submit should have some interaction with the user when
 * auto-submit is blocked (see `start()`).
 *
 * A step may send a `changed` event when its internal state has
 * changed. In this case, a widget might want to update itself to
 * reflect the change.
 *
 * Because `Wizard` is a subclass of this class, a wizard can be
 * embedded as a step of another wizard. This relation implements the
 * Composite design pattern.
 *
 * If you need a new step, create a subclass and override these
 * methods: `_doSubmit()`, `getRenderable()`, `_isCompleted()`,
 * `_shouldSubmitOnStart().  Read the documentation of these methods
 * for details.
 *
 * @abstract
 * @param {function} [spec.submitCallback] - If provided, this function
 * is called when the step is submitted. The function receives an
 * `onDone` argument that the function should call when it has
 * finished submitting. The function also receives the receiver as the
 * `step` argument.
 * @param {function} [spec.shouldSubmitOnStartFromContext] - Optional
 * function to determine whether the step should submit on start given
 * the context. Defaults to returning false.
 */
export default class AbstractWizardStep {
	constructor({
		submitCallback = () => {},
		shouldSubmitOnStartFromContext = () => false,
		canGoToPreviousStep = true,
	} = {}) {
		this._submitCallback = submitCallback;

		this._context = undefined;
		this._submitExceptions = new AlertCollector();
		this._submitInProgress = false;
		this._shouldSubmitOnStartFromContext = shouldSubmitOnStartFromContext;
		this._canGoToPreviousStep = canGoToPreviousStep;

		this.completed = event();
		this.submitted = event();
		this.submitSuccess = event();
		this.submitException = event();
		this.changed = event();
	}

	/**
	 * Return an instance of `validationResultCollector` containing
	 * the result of the previous `submit()`.
	 */
	getSubmitExceptions() {
		return this._submitExceptions;
	}

	/**
	 * Add a submit error to the receiver. This can be useful when the
	 * step delegates part of its submission work.
	 */
	addSubmitError(description) {
		this._submitExceptions.addError(description);
	}

	/**
	 * Start or restart the receiver.
	 *
	 * @param {object} context - Any object that will be passed around and
	 * enriched by the receiver.
	 * @param {boolean} preventSubmitOnStart - Some steps might, in
	 * some cases, submit automatically when started. Pass `true` to
	 * prevent that from happening.
	 */
	start({ context = {}, preventSubmitOnStart = false } = {}) {
		this._submitExceptions.reset();
		this._context = context;

		this._doStart();

		if (!preventSubmitOnStart && this._shouldSubmitOnStart()) {
			this.submit();
		}
	}

	isSubmitting() {
		return this._submitInProgress;
	}

	canBeSubmitted() {
		return this._isCompleted() && !this.isSubmitting();
	}

	/**
	 * Submit the receiver. This triggers a `submitted` event. As a
	 * result, either the `submitSuccess` or `submitException` event will
	 * be asynchronously triggered. In both cases, the context object
	 * might be populated with more data.
	 *
	 * This method should only be called if `canBeSubmitted()` returns
	 * true;
	 */
	submit() {
		this._submitExceptions.reset();
		this._submitInProgress = true;
		this.submitted.trigger();

		this._doSubmit({
			// Force asynchronous event submission so that synchronous
			// and asynchronous steps behave similarly.
			onDone: () =>
				setTimeout(() => {
					this._submitInProgress = false;

					if (this._submitExceptions.isEmpty()) {
						this.submitSuccess.trigger();
					} else {
						this.submitException.trigger();
					}
				}, 0),
		});
	}

	/**
	 * Return a renderable representing the receiver.
	 */
	getRenderable() {
		return this._subclassResponsibility();
	}

	getTitle() {
		return undefined;
	}

	canGoToPreviousStep() {
		return this._canGoToPreviousStep;
	}

	/**
	 * Return true iff the receiver is ready to be submitted.
	 */
	_isCompleted() {
		throw new Error("Subclass responsibility");
	}

	/**
	 * Override this method if the receiver may auto-submit when it is
	 * started.
	 */
	_shouldSubmitOnStart() {
		return this._shouldSubmitOnStartFromContext(this._context);
	}

	/**
	 * Override this to implement the behavior of the step when it is
	 * submitted. If the receiver detects any problem, warnings and
	 * exceptions must be added to `this._submitExceptions`. At the
	 * end, `onDone` must be called.
	 *
	 * @param {function} onDone - This function should be called when
	 * the step finishes what it should do. Populate
	 * `this._submitExceptions` before calling `onDone()`.
	 */
	_doSubmit({ onDone }) {
		this._submitCallback({
			step: this,
			onDone,
		});
	}

	_doStart() {
		// Please override me if you want to do something specific
		// when the receiver starts.
	}
}
